import * as React from 'react'
import { useTranslation } from 'hooks'
import PriceRangeSlider from './PriceRangeSlider'

// Filter state the island owns and forwards to the feed. `facetFilters` mirrors
// the backend's `facet_filters[key][]` shape (OR within a key, AND across keys).
// consignment / inventory are tri-state: true (in), false (not), null (unset).
export interface SellingFilterState {
  facetFilters: Record<string, string[]>
  priceMinCents: number | null
  priceMaxCents: number | null
  consignment: boolean | null
  inventory: boolean | null
}

export const EMPTY_FILTERS: SellingFilterState = {
  facetFilters: {},
  priceMinCents: null,
  priceMaxCents: null,
  consignment: null,
  inventory: null
}

// One size_conversion the result set contains: its region labels + whether it's
// a grade (drives the Size / Grade / Size / Grade heading), ordered by position.
export interface SizeOption {
  id: number
  uk: string
  eu: string
  us: string
  grade: boolean
  position: number | null
}

export interface FilterFlags {
  consignment: boolean
  inventory: boolean
}

type FacetCounts = Record<string, Record<string, number>>
type PriceBounds = { min_cents: number; max_cents: number } | null

interface Props {
  admin: boolean
  facets: FacetCounts
  availableFacets: string[]
  priceBounds: PriceBounds
  sizeOptions: SizeOption[]
  filterFlags: FilterFlags
  sortOptions: string[]
  sort: string
  onSortChange: (value: string) => void
  filters: SellingFilterState
  onChange: (next: SellingFilterState) => void
}

// `t` has to be passed explicitly to the helpers below: they are module-scope
// functions, not components, so they can't call `useTranslation` themselves.
type Translate = (key: string, options?: Record<string, unknown>) => string

const I18N_ROOT = 'react_components.selling_search'

// Label for a sort key the feed advertises in `sort_options`. `defaultValue`
// keeps a NEW server-side sort readable the moment it ships, before its copy
// exists — the alternative is the raw key or i18n-js's "missing translation".
const sortLabel = (t: Translate, key: string): string =>
  t(`${I18N_ROOT}.sorts.${key}`, { defaultValue: humanise(key) })

type SizeRegion = 'UK' | 'EU' | 'US'
const REGIONS: SizeRegion[] = ['UK', 'EU', 'US']

// The size facet is filtered on the value column for the active region. The chips
// group by their displayed value, so two size_conversions that read the same
// (e.g. two ids both "UK 9") collapse to one chip that filters on the value.
const SIZE_KEYS: Record<SizeRegion, string> = {
  UK: 'size_conversion.uk',
  EU: 'size_conversion.eu',
  US: 'size_conversion.us'
}
const SIZE_KEY_SET = new Set<string>(Object.values(SIZE_KEYS))

// Friendly group headings for the generic facet chips (brand / type / seller
// attributes). Anything not listed falls back to a humanised key.
const FACET_LABEL_KEYS: Record<string, string> = {
  'brand.name': 'brand',
  'product_type.name': 'product_type',
  'user.role': 'account_type',
  'user.seller_type': 'seller_type',
  'user.business_type': 'business_type'
}

// Facets rendered by their own bespoke control rather than a generic chip row.
const SPECIAL_FACETS = [...SIZE_KEY_SET, 'status']

const humanise = (value: string): string => {
  const spaced = value.replace(/_/g, ' ')
  const capitalised = spaced.charAt(0).toUpperCase() + spaced.slice(1)

  // VAT reads better upper-cased than "Standard vat"
  return capitalised.replace(/\bvat\b/i, 'VAT')
}

const facetLabel = (t: Translate, key: string): string => {
  const localeKey = FACET_LABEL_KEYS[key]

  return localeKey ? t(`${I18N_ROOT}.facets.${localeKey}`) : humanise(key)
}

const regionValue = (option: SizeOption, region: SizeRegion): string => {
  if (region === 'EU') return option.eu
  if (region === 'US') return option.us
  return option.uk
}

// Distinct display values for a set of size options in the given region, ordered
// by their lowest position — collapses size_conversions that share a label into
// one chip so the same size never appears twice.
const dedupeSizeValues = (
  options: SizeOption[],
  region: SizeRegion
): string[] => {
  const minPosition = new Map<string, number>()

  for (const option of options) {
    const value = regionValue(option, region)

    if (!value) continue

    const position = option.position ?? 0
    const existing = minPosition.get(value)

    if (existing === undefined || position < existing) {
      minPosition.set(value, position)
    }
  }

  return [...minPosition.keys()].sort(
    (a, b) => (minPosition.get(a) ?? 0) - (minPosition.get(b) ?? 0)
  )
}

const countActive = (f: SellingFilterState): number =>
  Object.values(f.facetFilters).reduce((n, v) => n + v.length, 0) +
  (f.priceMinCents != null || f.priceMaxCents != null ? 1 : 0) +
  (f.consignment != null ? 1 : 0) +
  (f.inventory != null ? 1 : 0)

// values for a facet, most-common first (backend already count-orders, but the
// hash order isn't guaranteed across the wire so re-sort defensively)
const facetValues = (facets: FacetCounts, key: string): string[] =>
  Object.entries(facets[key] ?? {})
    .sort((a, b) => b[1] - a[1])
    .map(([value]) => value)

const SellingFilters: React.FC<Props> = ({
  admin,
  facets,
  availableFacets,
  priceBounds,
  sizeOptions,
  filterFlags,
  sortOptions,
  sort,
  onSortChange,
  filters,
  onChange
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const activeCount = countActive(filters)

  const toggleFacet = (key: string, value: string) => {
    const current = filters.facetFilters[key] ?? []
    const nextValues = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    const nextFacets = { ...filters.facetFilters }

    if (nextValues.length) nextFacets[key] = nextValues
    else delete nextFacets[key]
    onChange({ ...filters, facetFilters: nextFacets })
  }

  // Switching size region drops the departing region's SIZE selections — a
  // listing has one size, so keeping uk=9 while now filtering eu=44 would match
  // nothing. Only the passed size values are removed, so grade chips (which live
  // on the uk key but aren't passed here) survive the switch.
  const clearRegionSizes = (key: string, values: string[]) => {
    const current = filters.facetFilters[key] ?? []
    const nextValues = current.filter((v) => !values.includes(v))
    const nextFacets = { ...filters.facetFilters }

    if (nextValues.length) nextFacets[key] = nextValues
    else delete nextFacets[key]
    onChange({ ...filters, facetFilters: nextFacets })
  }

  // consignment / inventory chips flip between their value and unset.
  const setTri = (field: 'consignment' | 'inventory', value: boolean) =>
    onChange({ ...filters, [field]: filters[field] === value ? null : value })

  const genericFacets = availableFacets.filter(
    (k) => !SPECIAL_FACETS.includes(k)
  )
  const showSize = sizeOptions.length > 0
  const showConsignment = filterFlags.consignment || filters.consignment != null
  const showInventory =
    admin && (filterFlags.inventory || filters.inventory != null)
  const hasPrice = priceBounds != null

  const nothingToFilter =
    !showSize &&
    genericFacets.length === 0 &&
    !hasPrice &&
    !showConsignment &&
    !showInventory

  // Keep the bar when there's a sort menu even if there's nothing to filter.
  if (nothingToFilter && sortOptions.length === 0 && activeCount === 0) {
    return null
  }

  const showFilterToggle = !nothingToFilter || activeCount > 0

  return (
    <div className="selling-filters">
      <div className="selling-filters__bar">
        {showFilterToggle && (
          <button
            type="button"
            className={`selling-filters__toggle${open ? ' is-open' : ''}`}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <FilterIcon />
            Filters
            {activeCount > 0 && (
              <span className="selling-filters__badge">{activeCount}</span>
            )}
          </button>
        )}

        <ActiveChips
          filters={filters}
          sizeOptions={sizeOptions}
          onChange={onChange}
        />

        {activeCount > 0 && (
          <button
            type="button"
            className="selling-filters__clear"
            onClick={() => onChange(EMPTY_FILTERS)}
          >
            Clear all
          </button>
        )}

        {sortOptions.length > 0 && (
          <label className="selling-filters__sort" title="Sort">
            <SortIcon />
            <select
              className="selling-filters__sort-select"
              value={sort}
              onChange={(event) => onSortChange(event.target.value)}
              aria-label={t(`${I18N_ROOT}.sort_results`)}
            >
              <option value="">{t(`${I18N_ROOT}.sort`)}</option>
              {sortOptions.map((key) => (
                <option key={key} value={key}>
                  {sortLabel(t, key)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {open && (
        <div className="selling-filters__panel">
          {hasPrice && priceBounds && (
            <section className="selling-filters__group">
              <h4 className="selling-filters__label">Price</h4>
              <PriceRangeSlider
                minBoundCents={priceBounds.min_cents}
                maxBoundCents={priceBounds.max_cents}
                minCents={filters.priceMinCents}
                maxCents={filters.priceMaxCents}
                onChange={(min, max) =>
                  onChange({
                    ...filters,
                    priceMinCents: min,
                    priceMaxCents: max
                  })
                }
              />
            </section>
          )}

          {showSize && (
            <SizeSection
              sizeOptions={sizeOptions}
              facetFilters={filters.facetFilters}
              onToggle={toggleFacet}
              onClearRegion={clearRegionSizes}
            />
          )}

          {genericFacets.map((key) => (
            <FacetSection
              key={key}
              facets={facets}
              filterKey={key}
              selected={filters.facetFilters[key] ?? []}
              onToggle={(value) => toggleFacet(key, value)}
            />
          ))}

          {showConsignment && (
            <TriStateGroup
              label={t(`${I18N_ROOT}.consignment`)}
              value={filters.consignment}
              inLabel={t(`${I18N_ROOT}.in_consignment`)}
              outLabel={t(`${I18N_ROOT}.not_consigned`)}
              onSet={(value) => setTri('consignment', value)}
            />
          )}

          {showInventory && (
            <TriStateGroup
              label={t(`${I18N_ROOT}.inventory`)}
              value={filters.inventory}
              inLabel={t(`${I18N_ROOT}.in_inventory`)}
              outLabel={t(`${I18N_ROOT}.not_in_inventory`)}
              onSet={(value) => setTri('inventory', value)}
            />
          )}
        </div>
      )}
    </div>
  )
}

// Size chips group by their displayed value for the active region (UK/EU/US), so
// two size_conversions that read the same collapse to one chip that filters on the
// value. Switching region filters a different column and drops the previous
// region's size selection (a listing has one size). Grades read the same in any
// region (their label lives in `uk`), so they render region-independently and the
// region toggle only shows when there are true sizes.
const SizeSection: React.FC<{
  sizeOptions: SizeOption[]
  facetFilters: Record<string, string[]>
  onToggle: (key: string, value: string) => void
  onClearRegion: (key: string, values: string[]) => void
}> = ({ sizeOptions, facetFilters, onToggle, onClearRegion }) => {
  const { t } = useTranslation()
  const [region, setRegion] = React.useState<SizeRegion>('UK')
  const sizes = sizeOptions.filter((o) => !o.grade)
  const grades = sizeOptions.filter((o) => o.grade)
  const hasSizes = sizes.length > 0
  const hasGrades = grades.length > 0
  const headingKey =
    hasGrades && hasSizes ? 'size_or_grade' : hasGrades ? 'grade' : 'size'
  const heading = t(`${I18N_ROOT}.${headingKey}`)

  const sizeKey = SIZE_KEYS[region]
  const gradeKey = SIZE_KEYS.UK // grade labels live in the uk column
  const sizeValues = dedupeSizeValues(sizes, region)
  const gradeValues = dedupeSizeValues(grades, 'UK')
  const selectedSizes = facetFilters[sizeKey] ?? []
  const selectedGrades = facetFilters[gradeKey] ?? []

  const pickRegion = (next: SizeRegion) => {
    if (next === region) return
    onClearRegion(sizeKey, sizeValues) // drop this region's size selection
    setRegion(next)
  }

  return (
    <section className="selling-filters__group">
      <div className="selling-filters__label-row">
        <h4 className="selling-filters__label">{heading}</h4>
        {hasSizes && (
          <div
            className="selling-filters__segmented"
            role="tablist"
            aria-label={t(`${I18N_ROOT}.size_system`)}
          >
            {REGIONS.map((r) => (
              <button
                key={r}
                type="button"
                role="tab"
                aria-selected={region === r}
                className={`selling-filters__segment${
                  region === r ? ' is-active' : ''
                }`}
                onClick={() => pickRegion(r)}
              >
                {r}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="selling-filters__chips">
        {hasSizes &&
          sizeValues.map((value) => (
            <Toggle
              key={`size-${value}`}
              label={value}
              on={selectedSizes.includes(value)}
              onClick={() => onToggle(sizeKey, value)}
            />
          ))}
        {gradeValues.map((value) => (
          <Toggle
            key={`grade-${value}`}
            label={value}
            on={selectedGrades.includes(value)}
            onClick={() => onToggle(gradeKey, value)}
          />
        ))}
      </div>
    </section>
  )
}

const FacetSection: React.FC<{
  facets: FacetCounts
  filterKey: string
  selected: string[]
  onToggle: (value: string) => void
}> = ({ facets, filterKey, selected, onToggle }) => {
  const { t } = useTranslation()

  return (
    <section className="selling-filters__group">
      <h4 className="selling-filters__label">{facetLabel(t, filterKey)}</h4>
      <div className="selling-filters__chips">
        {facetValues(facets, filterKey).map((value) => (
          <Toggle
            key={value}
            label={humanise(value)}
            on={selected.includes(value)}
            onClick={() => onToggle(value)}
          />
        ))}
      </div>
    </section>
  )
}

// Tri-state pair: each chip toggles between its direction and unset; picking one
// direction clears the other (single underlying value).
const TriStateGroup: React.FC<{
  label: string
  value: boolean | null
  inLabel: string
  outLabel: string
  onSet: (value: boolean) => void
}> = ({ label, value, inLabel, outLabel, onSet }) => (
  <section className="selling-filters__group">
    <h4 className="selling-filters__label">{label}</h4>
    <div className="selling-filters__chips">
      <Toggle label={inLabel} on={value === true} onClick={() => onSet(true)} />
      <Toggle
        label={outLabel}
        on={value === false}
        onClick={() => onSet(false)}
      />
    </div>
  </section>
)

const Toggle: React.FC<{
  label: string
  on: boolean
  onClick: () => void
}> = ({ label, on, onClick }) => (
  <button
    type="button"
    aria-pressed={on}
    className={`selling-filters__chip${on ? ' is-on' : ''}`}
    onClick={onClick}
  >
    {label}
  </button>
)

const poundsLabel = (cents: number): string =>
  `£${(cents / 100).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

interface ActiveChip {
  label: string
  remove: () => void
}

const facetChipLabel = (
  t: Translate,
  key: string,
  value: string,
  sizeOptions: SizeOption[]
): string => {
  if (!SIZE_KEY_SET.has(key)) return `${facetLabel(t, key)}: ${humanise(value)}`

  const isGrade =
    key === SIZE_KEYS.UK && sizeOptions.some((o) => o.grade && o.uk === value)

  const heading = t(`${I18N_ROOT}.${isGrade ? 'grade' : 'size'}`)

  return `${heading}: ${value}`
}

const withoutFacetValue = (
  filters: SellingFilterState,
  key: string,
  value: string
): SellingFilterState => {
  const nextValues = (filters.facetFilters[key] ?? []).filter(
    (v) => v !== value
  )
  const nextFacets = { ...filters.facetFilters }

  if (nextValues.length) nextFacets[key] = nextValues
  else delete nextFacets[key]
  return { ...filters, facetFilters: nextFacets }
}

const facetChips = (
  t: Translate,
  filters: SellingFilterState,
  sizeOptions: SizeOption[],
  onChange: (next: SellingFilterState) => void
): ActiveChip[] =>
  Object.entries(filters.facetFilters).flatMap(([key, values]) =>
    values.map((value) => ({
      label: facetChipLabel(t, key, value, sizeOptions),
      remove: () => onChange(withoutFacetValue(filters, key, value))
    }))
  )

const scalarChips = (
  t: Translate,
  filters: SellingFilterState,
  onChange: (next: SellingFilterState) => void
): ActiveChip[] => {
  const chips: ActiveChip[] = []

  if (filters.priceMinCents != null || filters.priceMaxCents != null) {
    const min =
      filters.priceMinCents != null ? poundsLabel(filters.priceMinCents) : '0'
    const max =
      filters.priceMaxCents != null
        ? poundsLabel(filters.priceMaxCents)
        : t(`${I18N_ROOT}.price_any`)

    chips.push({
      label: t(`${I18N_ROOT}.price_range`, { min, max }),
      remove: () =>
        onChange({ ...filters, priceMinCents: null, priceMaxCents: null })
    })
  }

  if (filters.consignment != null) {
    chips.push({
      label: t(
        `${I18N_ROOT}.${
          filters.consignment ? 'in_consignment' : 'not_consigned'
        }`
      ),
      remove: () => onChange({ ...filters, consignment: null })
    })
  }

  if (filters.inventory != null) {
    chips.push({
      label: t(
        `${I18N_ROOT}.${
          filters.inventory ? 'in_inventory' : 'not_in_inventory'
        }`
      ),
      remove: () => onChange({ ...filters, inventory: null })
    })
  }

  return chips
}

const buildActiveChips = (
  t: Translate,
  filters: SellingFilterState,
  sizeOptions: SizeOption[],
  onChange: (next: SellingFilterState) => void
): ActiveChip[] => [
  ...facetChips(t, filters, sizeOptions, onChange),
  ...scalarChips(t, filters, onChange)
]

const ActiveChips: React.FC<{
  filters: SellingFilterState
  sizeOptions: SizeOption[]
  onChange: (next: SellingFilterState) => void
}> = ({ filters, sizeOptions, onChange }) => {
  const { t } = useTranslation()

  return (
    <div className="selling-filters__active">
      {buildActiveChips(t, filters, sizeOptions, onChange).map((chip) => (
        <button
          key={chip.label}
          type="button"
          className="selling-filters__active-chip"
          onClick={chip.remove}
          aria-label={t(`${I18N_ROOT}.remove_filter`, { filter: chip.label })}
        >
          {chip.label}
          <CrossIcon />
        </button>
      ))}
    </div>
  )
}

const FilterIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="7" y1="12" x2="17" y2="12" />
    <line x1="10" y1="18" x2="14" y2="18" />
  </svg>
)

// Bidirectional up/down arrows — reads as "sort" without a text label.
const SortIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 4v16" />
    <path d="M4 7l3-3 3 3" />
    <path d="M17 20V4" />
    <path d="M14 17l3 3 3-3" />
  </svg>
)

const CrossIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

export default SellingFilters
